import type { AutomationStatus } from '../../api/automations';
import { slotOfRun } from './schedule';

/**
 * The statuses the Automations page and Home's "Needs attention" line share.
 * One definition, so both pages count the same jobs.
 */
type AutomationRowData = AutomationStatus;

export type DisplayStatus = AutomationStatus['status'] | 'paused' | 'idle' | 'missed';


/**
 * The scheduled slot the job did not run in, and why. The API computes it
 * from the cron (`missedSlot`); an API from before that is approximated from
 * the last completed run, for healthy jobs only.
 */
export function slotMiss(a: AutomationRowData, now = new Date()): { at: Date; reason: 'no_run' | 'cancelled' | 'restart' | null } | null {
  if (a.missedSlot !== undefined) return a.missedSlot ? { at: new Date(a.missedSlot.at), reason: a.missedSlot.reason } : null;
  if (a.status !== 'healthy' || a.paused || !a.lastCompletedAt) return null;
  const slot = slotOfRun(a.schedule, a.scheduleHuman, new Date(now.getTime() - 10 * 60_000));
  if (!slot) return null;
  return new Date(a.lastCompletedAt).getTime() < slot.getTime() - 2 * 60_000 ? { at: slot, reason: null } : null;
}

type HistoryRunLite = { state: string; createdOn: string; startedOn: string | null };
type SlotMissData = { at: Date; reason: 'no_run' | 'cancelled' | 'restart' | null };

/**
 * Reads the slot against the run history the dialog (or row) already has, so
 * the header never contradicts its own list: a run that started in the slot
 * and was cancelled makes it "cancelled", whatever an older API guessed.
 */
export function slotMissWithHistory(miss: SlotMissData | null, runs: readonly HistoryRunLite[] | undefined, a: AutomationRowData): SlotMissData | null {
  if (!miss || !runs || (miss.reason !== null && miss.reason !== 'no_run')) return miss;
  const inSlot = runs.some(
    (r) => r.state === 'cancelled' && slotOfRun(a.schedule, a.scheduleHuman, new Date(r.startedOn ?? r.createdOn))?.getTime() === miss.at.getTime(),
  );
  return inSlot ? { ...miss, reason: 'cancelled' } : miss;
}

/** Start of the newest cancelled run that began after the last finished one ("Last: 13 Oct 08:15 · Cancelled"). */
export function cancelledAfterLastRun(runs: readonly HistoryRunLite[] | undefined, lastRunAt: string | null): string | null {
  if (!runs || !lastRunAt) return null;
  const after = new Date(lastRunAt).getTime();
  let best: string | null = null;
  for (const r of runs) {
    if (r.state !== 'cancelled') continue;
    const at = r.startedOn ?? r.createdOn;
    if (new Date(at).getTime() > after && (best === null || new Date(at).getTime() > new Date(best).getTime())) best = at;
  }
  return best;
}


/**
 * The longest gap a cron schedule should leave between two runs, with slack.
 * Null when the schedule is not a plain 5-field cron (manual, webhook…).
 */
function expectedMaxGapMs(cron: string): number | null {
  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;
  const [, hour, dom, month, dow] = parts;
  const DAY = 86_400_000;
  if (month !== '*') return 400 * DAY;
  if (dom !== '*') return 40 * DAY;
  if (dow !== '*') return 9 * DAY;
  if (hour === '*' || hour.startsWith('*/')) return 1 * DAY;
  return 3 * DAY;
}

/**
 * The API has no paused flag, so a job whose last good run is far older than
 * its schedule allows would otherwise read as green "Healthy". Show it as
 * Paused (neutral) instead.
 */
export function displayStatus(a: AutomationRowData, now = Date.now()): DisplayStatus {
  if (a.paused && a.status !== 'running' && a.status !== 'stuck') return 'paused';
  // Runs on schedule but has nothing to act on (UptimeRobot's poll with no
  // connection): idle, not a green "Healthy".
  if (a.status === 'healthy' && a.idleReason) return 'idle';
  if (a.status !== 'healthy' || !a.lastRunAt) return a.status;
  const gap = expectedMaxGapMs(a.schedule);
  if (gap !== null && now - new Date(a.lastRunAt).getTime() > gap) return 'paused';
  // Healthy last run, but nothing ran in its latest slot: amber, never a failure.
  return slotMiss(a, new Date(now)) ? 'missed' : a.status;
}

/** A failure whose last run is this old is "stale": nothing has retried it since. */
const STALE_FAILURE_MS = 30 * 86_400_000;

export function isStaleFailure(a: AutomationStatus, now = Date.now()): boolean {
  return a.status === 'failed' && !!a.lastRunAt && now - new Date(a.lastRunAt).getTime() > STALE_FAILURE_MS;
}

/** "4 failed (1 stale)": stale failures are some of the failed, never counted beside them. Tile and headings say it the same way. */
export function failedLabel(jobs: AutomationStatus[], now = Date.now()): string | null {
  const failed = jobs.filter((a) => a.status === 'failed').length;
  if (failed === 0) return null;
  const stale = jobs.filter((a) => isStaleFailure(a, now)).length;
  return `${failed} failed${stale > 0 ? ` (${stale} stale)` : ''}`;
}


/**
 * Jobs that need a look: a failed run, a run stuck for over an hour, or a
 * scheduled slot nothing ran in. Automations (tile, group heading) and Home
 * both read these counts.
 */
export function attentionCounts(
  jobs: AutomationStatus[],
  now = Date.now(),
): { failed: number; stuck: number; missed: number; cancelled: number; total: number } {
  let failed = 0;
  let stuck = 0;
  let missed = 0;
  let cancelled = 0;
  for (const a of jobs) {
    if (a.status === 'failed') failed++;
    else if (a.status === 'stuck') stuck++;
    else if (displayStatus(a, now) === 'missed') {
      // A run that started in the slot and was cancelled is "cancelled", not "missed".
      if (slotMiss(a, new Date(now))?.reason === 'cancelled') cancelled++;
      else missed++;
    }
  }
  return { failed, stuck, missed, cancelled, total: failed + stuck + missed + cancelled };
}

/** "3 failed · 1 cancelled": every part that is more than zero. */
export function attentionParts(c: { failed: number; stuck: number; missed: number; cancelled?: number }): string[] {
  return [
    c.failed > 0 ? `${c.failed} failed` : null,
    c.stuck > 0 ? `${c.stuck} stuck` : null,
    c.missed > 0 ? `${c.missed} missed` : null,
    c.cancelled ? `${c.cancelled} cancelled` : null,
  ].filter(
    (p): p is string => p !== null,
  );
}

/** A job nobody schedules (manual or webhook) that has never run: not an exception. */
export function isManualNeverRan(a: AutomationStatus): boolean {
  return a.status === 'unknown' && a.schedule.trim().split(/\s+/).length !== 5;
}

/** "dmsSent" -> "DMs sent", "rowsFound" -> "rows found". */
function countLabel(key: string): string {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return words.replace(/\bdms\b/, 'DMs').replace(/\bid(s)?\b/, 'ID$1');
}

/**
 * What a finished run did, read from its output: a `summary` text when a step
 * wrote one, else the numeric counts the steps returned ("1 candidate" style:
 * "3 candidates · 2 drafted · 2 DMs sent"). Null when the output holds neither,
 * so the caller can say "no output recorded" instead of inventing a result.
 */
export function runOutputSummary(output: unknown): string | null {
  if (!output || typeof output !== 'object') return null;
  const root = output as Record<string, unknown>;
  if (typeof root.summary === 'string' && root.summary.trim()) return root.summary.trim();
  const steps = root.stepResults && typeof root.stepResults === 'object' ? Object.values(root.stepResults as Record<string, unknown>) : [root];
  const parts: string[] = [];
  for (const step of steps) {
    if (!step || typeof step !== 'object') continue;
    const s = step as Record<string, unknown>;
    if (typeof s.summary === 'string' && s.summary.trim()) return s.summary.trim();
    for (const [k, v] of Object.entries(s)) {
      if (typeof v === 'number' && Number.isFinite(v)) parts.push(`${v} ${countLabel(k)}`);
    }
  }
  return parts.length > 0 ? parts.slice(0, 4).join(' · ') : null;
}
